import { gsap, ScrollTrigger } from "../core/gsap";
import { initLineAnimations } from "../line-animation";

const SELECTOR_SECTION = '[data-collage-image-video]';
const SELECTOR_FRAME = '[data-frame-animation]';
const SELECTOR_MEDIA_PARENT = '[data-parallax-parent]';

// Radial wipe animation to imitate line draw effect
function updateRadialWipe(element: HTMLElement, angle: number, centerX: number, centerY: number, maxRadius: number) {
  const RAD = Math.PI / 180;
  const PI_2 = Math.PI / 2;
  
  // Create clipPath polygon that creates radial sweep effect
  let clipPath;
  if (angle >= 360) {
    // Full reveal
    clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
  } else if (angle <= 0) {
    // Start state - single point at center
    clipPath = `polygon(${centerX}px ${centerY}px, ${centerX}px ${centerY}px, ${centerX}px ${centerY}px, ${centerX}px ${centerY}px)`;
  } else {
    // Create polygon that sweeps from center outward
    const points = [`${centerX}px ${centerY}px`]; // Center point
    
    // Add points along the arc from 0 to current angle
    for (let i = 0; i <= angle; i += 5) {
      const a = i * RAD - PI_2; // Start from top (-90 degrees)
      const x = centerX + maxRadius * Math.cos(a);
      const y = centerY + maxRadius * Math.sin(a);
      points.push(`${x}px ${y}px`);
    }
    
    clipPath = `polygon(${points.join(', ')})`;
  }
  
  element.style.clipPath = clipPath;
}

export function initCollageImageVideo() {
  document.querySelectorAll<HTMLElement>(SELECTOR_SECTION).forEach((el) => {
    // Skip if already initialized
    if (el.dataset.gsapInit === "true") return;

    const frameElements = el.querySelectorAll<HTMLElement>(SELECTOR_FRAME);
    
    if (frameElements.length === 0) return;

    frameElements.forEach((frameEl) => {
      // Calculate dimensions once
      const rect = frameEl.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const maxRadius = Math.sqrt(centerX ** 2 + centerY ** 2);
    
      // Set initial clip-path (single point at center)
      gsap.set(frameEl, {
        opacity: 0.4,
        clipPath: `polygon(${centerX}px ${centerY}px, ${centerX}px ${centerY}px, ${centerX}px ${centerY}px, ${centerX}px ${centerY}px)`
      });
    
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: frameEl,
          start: "top 80%", 
          end: "top 0%", 
          toggleActions: "play none none none"
        }
      });
    
      // Animate custom angle
      tl.fromTo(frameEl, {
        '--wipe-angle': 0
      }, {
        '--wipe-angle': 360,
        duration: 1.5,
        ease: "power2.out",
        onUpdate: function() {
          const angle = parseFloat(getComputedStyle(frameEl).getPropertyValue('--wipe-angle')) || 0;
          updateRadialWipe(frameEl, angle, centerX, centerY, maxRadius);
        }
      });
    });

    // Handle line animations using reusable function
    initLineAnimations(el);

    const animatedParents = new Set<HTMLElement>();
    el.querySelectorAll<HTMLElement>(SELECTOR_MEDIA_PARENT).forEach((parent) => {
      if (animatedParents.has(parent)) return;
      animatedParents.add(parent);

      // Responsive animation configuration
      const getAnimationConfig = () => {
        const width = window.innerWidth;
        
        if (width < 768) {
          return { method: 'fromTo', from: { y: 40 }, to: { y: -20 }, device: 'mobile' };
        } else if (width < 1100) {
          return { method: 'fromTo', from: { y: 70 }, to: { y: -50 }, device: 'tablet' };
        } else {
          return { method: 'fromTo', from: { y: 40 }, to: { y: -70 }, device: 'desktop' };
        }
      };

      const config = getAnimationConfig();
      
      gsap.fromTo(parent, config.from!, {
        ...config.to!,
        ease: "none",
        scrollTrigger: {
          trigger: parent,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
    });

    // Mark as initialized
    el.dataset.gsapInit = "true";
  });
}
